import React, { PropsWithChildren, ReactElement, useState, useMemo, useCallback, useEffect } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import {
  FaCaretDown,
  FaCaretUp,
  FaFileAlt,
  FaLock,
  FaRegCalendarAlt,
  FaExclamationCircle,
  FaSuitcase,
} from 'react-icons/fa';

import styled from 'styled-components';

import API from 'core/Api';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { useWorkModeContext } from 'store/work-mode';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import copyToClipboard from 'utils/helpers/copyToClipboard';
import { buildClientEntity } from 'utils/helpers/client';
import { getUserInfo } from 'utils/auth';
import useWindowWidth from 'utils/helpers/useWindowWidth';

import SendDoc from 'components/documents/SendDoc';
import DeleteDoc from 'components/documents/DeleteDoc';
import ReviewDoc from 'components/documents/ReviewDoc';
import InformationDoc from 'components/documents/InformationDoc';
import FetchTable from 'components/table/FetchTable';
import ArchiveDoc from 'components/documents/ArchiveDoc';
import TooltipWrapper from 'components/TooltipWrapper';
import BadgeComponent from 'components/Badge';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { ISelectOptionProps } from 'components/form/types';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import MassDocEmployeesDetails from './MassDocEmployeesDetails';
import { IDocumentGroup } from './types';
import { ISelectActionButton } from '../types';

export interface IGroupedDocsTableProps<T extends Record<string, unknown>> {
  entityName: string;
  entityEndpoint: string;
  searchParams: {
    [key: string]: string;
  };
  hideActionsColumn?: boolean;
  isSelectable?: boolean;
  customSelectableRowProps?: string[];
  toggleSelectBtnLabel?: string;
  useCustomSelectBtn?: boolean;
  selectActionButtons?: ISelectActionButton[];
  disablePagination?: boolean;
  disableIconFilters?: boolean;
}

export interface CustomIconProps {
  isSelected: boolean;
  onClick: () => void;
}

type ButtonsType = {
  type: string;
  text: string;
  action_link?: string;
  disabled: boolean;
  disabled_reason: string;
};

interface IconWrapperProps {
  isSelected: boolean;
}

function GroupedDocsTable<T extends Record<string, unknown>>({
  entityName,
  entityEndpoint,
  searchParams,
  hideActionsColumn,
  isSelectable,
  customSelectableRowProps,
  useCustomSelectBtn,
  toggleSelectBtnLabel,
  selectActionButtons,
  disablePagination,
  disableIconFilters,
}: PropsWithChildren<IGroupedDocsTableProps<T>>): ReactElement {
  const [skipResetExpanded, setSkipResetExpanded] = useState(true);
  const [selectedOptionLeaves, setSelectedOptionLeaves] = useState('false');
  const [selectedOptionWorkTrips, setSelectedOptionWorkTrips] = useState('false');
  const [selectedOptionWait, setSelectedOptionWait] = useState('false');
  const [selectedOptionHealthAndSafety, setSelectedOptionHealthAndSafety] = useState('false');
  const [fetchedData, setFetchedData] = useState<IDocumentGroup>();
  const queryClient = useQueryClient();
  const { client } = useActiveClientContext();
  const { t } = useTranslation();
  const { workMode } = useWorkModeContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const userInfo = getUserInfo();
  const isFeatureHealthAndSafetyEnabled = userInfo?.featureHealthAndSafetyTraining;
  const isLanguageEN = language === 'en';
  const isEmployee = workMode === 'employee';

  const windowWidth = useWindowWidth();

  const isMobile = windowWidth < 768;

  const handleDataFetched = (data: any) => {
    setFetchedData(data.items);
  };

  const {
    isLoading: isLoadingDocDropdowns,
    error: errorDocDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('document-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('document-holders/dropdown-options').then(res => res.data && res.data[language || 'bg']);
    }
  );

  const CustomIconLeaves: React.FC<CustomIconProps> = useCallback(
    ({ isSelected, onClick }) => {
      const tooltipMessage = isSelected
        ? t('documents.table.notHiddenLeaveDocuments')
        : t('documents.table.hiddenLeaveDocuments');
      return (
        <TooltipWrapper
          overlay={tooltipMessage}
          id="customIconTooltip"
        >
          <IconWrapper
            isSelected={isSelected}
            onClick={onClick}
          >
            <FaRegCalendarAlt />
          </IconWrapper>
        </TooltipWrapper>
      );
    },
    [t]
  );

  const CustomIconWait: React.FC<CustomIconProps> = useCallback(
    ({ isSelected, onClick }) => {
      const tooltipMessage = isSelected
        ? t('documents.table.showOnlyWaitingAction')
        : t('documents.table.showAllDocuments');
      return (
        <TooltipWrapper
          overlay={tooltipMessage}
          id="customIconTooltip"
        >
          <IconWrapper
            isSelected={isSelected}
            onClick={onClick}
          >
            <FaExclamationCircle />
          </IconWrapper>
        </TooltipWrapper>
      );
    },
    [t]
  );

  const CustomIconWorkTrips: React.FC<CustomIconProps> = useCallback(
    ({ isSelected, onClick }) => {
      const tooltipMessage = isSelected
        ? t('documents.table.notHiddenWorkTripDocuments')
        : t('documents.table.hiddenWorkTripDocuments');
      return (
        <TooltipWrapper
          overlay={tooltipMessage}
          id="customIconTooltip"
        >
          <IconWrapper
            isSelected={isSelected}
            onClick={onClick}
          >
            <FaSuitcase />
          </IconWrapper>
        </TooltipWrapper>
      );
    },
    [t]
  );

  const CustomIconHealthAndSafety: React.FC<CustomIconProps> = useCallback(
    ({ isSelected, onClick }) => {
      const tooltipMessage = isSelected
        ? t('documents.table.notHiddenHealthAndSafetyDocuments')
        : t('documents.table.hiddenHealthAndSafetyDocuments');
      return (
        <TooltipWrapper
          overlay={tooltipMessage}
          id="customIconTooltip"
        >
          <IconWrapper
            isSelected={isSelected}
            onClick={onClick}
          >
            <FaFileAlt />
          </IconWrapper>
        </TooltipWrapper>
      );
    },
    [t]
  );

  const toggleSelectedOptionLeaves = () => {
    setSelectedOptionLeaves(prevOption => (prevOption === 'true' ? 'false' : 'true'));
  };

  const toggleSelectedOptionWait = () => {
    setSelectedOptionWait(prevOption => (prevOption === 'true' ? 'false' : 'true'));
  };

  const toggleSelectedOptionWorkTrips = () => {
    setSelectedOptionWorkTrips(prevOption => (prevOption === 'true' ? 'false' : 'true'));
  };

  const toggleSelectedOptionHealthAndSafety = () => {
    setSelectedOptionHealthAndSafety(prevOption => (prevOption === 'true' ? 'false' : 'true'));
  };

  const mobileColumns: Array<Column<IDocumentGroup>> = [
    {
      Header: t('documents.table.name') || '',
      accessor: 'name',
      Cell: ({ value, row }) => {
        const { name_en: nameEn, badge, has_locked_docs: hasLockedDocs, leaves_feature: leavesFeature } = row.original;
        const tooltip = leavesFeature ? 'documents.table.docFromLeavesFeature' : 'documents.table.docActionsInProgress';

        return (
          <span key={row.index}>
            {hasLockedDocs && (
              <div className="d-inline-block mr-2">
                <TooltipWrapper
                  id="tooltip-locked-holder"
                  overlay={t(tooltip)}
                >
                  <FaLock />
                </TooltipWrapper>
              </div>
            )}
            <TooltipWrapper
              id="tooltip-docName"
              overlay={isLanguageEN ? nameEn || value : value}
            >
              <span className="mr-2">{isLanguageEN ? nameEn || value : value}</span>
              {badge && renderBadge(badge)}
            </TooltipWrapper>
          </span>
        );
      },
    },
    {
      // Build our expander column
      Header: () => <Heading>#</Heading>,
      id: 'expander', // Make sure it has an ID
      disableSortBy: true,
      width: windowWidth > 1600 && windowWidth < 1921 ? '6%' : '8%',
      Cell: ({ row }) => (
        // Use Cell to render an expander for each row.
        // We can use the getToggleRowExpandedProps prop-getter
        // to build the expander.
        <div
          key={row.index}
          className="d-flex flex-row-reverse flex-md-row"
        >
          <span
            className="text-center text-md-left"
            aria-label="Expand document"
          >
            {row.isExpanded ? (
              <FaCaretUp
                size={22}
                color="#32bfa3"
              />
            ) : (
              <FaCaretDown size={22} />
            )}
          </span>
          <TooltipWrapper
            id="tooltip-totalDocs"
            overlay={t('documents.table.totalDocuments')}
            wrapperClass="text-center"
          >
            <span className="d-inline-flex align-items-center ml-md-3">
              {row.original.total_documents}
              <FaFileAlt
                size={14}
                style={{ marginLeft: 6 }}
              />
            </span>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  const columns: Array<Column<IDocumentGroup>> = [
    {
      // Build our expander column
      Header: () => <Heading>#</Heading>,
      id: 'expander', // Make sure it has an ID
      disableSortBy: true,
      width: windowWidth > 1600 ? '4%' : '6%',
      Cell: ({ row }) => (
        // Use Cell to render an expander for each row.
        // We can use the getToggleRowExpandedProps prop-getter
        // to build the expander.
        <div
          key={row.index}
          className="d-flex flex-column-reverse flex-md-row"
        >
          <span className="text-center text-md-left">
            {row.isExpanded ? (
              <FaCaretUp
                size={16}
                color="#32bfa3"
              />
            ) : (
              <FaCaretDown size={16} />
            )}
          </span>
          <TooltipWrapper
            id="tooltip-totalDocs"
            overlay={t('documents.table.totalDocuments')}
            wrapperClass="text-center"
          >
            <span className="d-inline-flex align-items-center ml-md-3">
              {row.original.total_documents}
              <FaFileAlt
                size={14}
                style={{ marginLeft: 6 }}
              />
            </span>
          </TooltipWrapper>
        </div>
      ),
    },
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('documents.table.name') || '',
      accessor: 'name',
      ...(windowWidth > 1920 ? { width: '20%' } : {}),
      Cell: ({ value, row }) => {
        const { name_en: nameEn, badge, has_locked_docs: hasLockedDocs, leaves_feature: leavesFeature } = row.original;
        const tooltip = leavesFeature ? 'documents.table.docFromLeavesFeature' : 'documents.table.docActionsInProgress';

        return (
          <span key={row.index}>
            {hasLockedDocs && (
              <div className="d-inline-block mr-2">
                <TooltipWrapper
                  id="tooltip-locked-holder"
                  overlay={t(tooltip)}
                >
                  <FaLock />
                </TooltipWrapper>
              </div>
            )}
            <TooltipWrapper
              id="tooltip-docName"
              overlay={isLanguageEN ? nameEn || value : value}
            >
              <span className="mr-2">{isLanguageEN ? nameEn || value : value}</span>
              {badge && renderBadge(badge)}
            </TooltipWrapper>
          </span>
        );
      },
    },
    {
      Header: t('documents.table.category') || '',
      accessor: 'category_name',
    },
    {
      Header: t('documents.table.author') || '',
      accessor: `${isLanguageEN ? 'creator_name_en' : 'creator_name'}`,
      Cell: ({ value, row }) => {
        const { name_en: nameEn, creator_name_en: creatorNameEn } = row.original;

        const tooltipWhenEn = isLanguageEN ? creatorNameEn : value;

        return (
          <TooltipWrapper
            key={row.index}
            id="tooltip-creatorName"
            overlay={nameEn ? tooltipWhenEn : value}
          >
            <span>{isLanguageEN && nameEn ? creatorNameEn || value : value}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('documents.table.employee') || '',
      accessor: `${isLanguageEN ? 'user_name_en' : 'user_name'}`,
      Cell: ({ value, row }) => {
        const { name_en: nameEn, user_name_en: userNameEn } = row.original;
        const tooltipWhenEn = isLanguageEN ? userNameEn : value;
        // When mass doc - show multi user label,
        // otherwise render user name.
        if (row.original.is_multi_user) return <MassDocEmployeesDetails id={row.original.id} />;

        return (
          <TooltipWrapper
            key={row.index}
            id="tooltip-userName"
            overlay={nameEn ? tooltipWhenEn : value}
          >
            <span>{isLanguageEN && nameEn ? userNameEn || value : value}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('documents.table.group') || '',
      accessor: 'group_names',
      Cell: ({ value, row }) => {
        const fullGroups = value.join(', ');
        const groupsPreview = fullGroups.length > 20 ? `${fullGroups.substr(0, 20)}...` : fullGroups;

        return (
          <TooltipWrapper
            key={row.index}
            id="tooltip-groups"
            overlay={`${t('common.copyToClipboard')}: ${fullGroups}`}
          >
            <span
              onClick={e => {
                copyToClipboard(fullGroups);
                e.stopPropagation();
              }}
            >
              {groupsPreview}
            </span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('documents.table.createdAt') || '',
      accessor: 'created_at',
      width: windowWidth < 1600 ? '15%' : '12%',
      Cell: ({ value, row }) => {
        const updatedAt = row.original.updated_at;
        return (
          <TooltipWrapper
            id="tooltip-updated-at"
            overlay={
              updatedAt
                ? `${t('documents.table.lastUpdate')}: ${formatDate(new Date(updatedAt), 'dd.mm.YYY hh:ii:ss')}`
                : ''
            }
          >
            <span>{formatDate(new Date(value), 'dd.mm.YYY hh:ii:ss')}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
      accessor: 'actions',
      width: windowWidth < 1600 ? '10%' : '5%',
      Cell: ({ row }) => (
        <div
          key={row.index}
          style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}
        >
          {renderActions(row.original.buttons, row.original.id, row.original.has_locked_docs, row.original.user_status)}
        </div>
      ),
    },
  ];

  const renderBadge = (badge: { type: string; text: string }) => {
    const variant: {
      [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'light' | 'dark';
    } = {
      waiting_action: 'primary',
      in_progress: 'warning',
      rejected: 'danger',
    };

    return (
      <BadgeComponent
        variant={variant[badge.type]}
        label={badge.text}
      />
    );
  };

  const renderActions = (
    buttons: ButtonsType[],
    documentId: string,
    hasLockedDocs: boolean,
    docHolderStatus: string
  ) => {
    const invalidateCallback = () => {
      setSkipResetExpanded(false);
      queryClient.invalidateQueries(entityName).then(() => {
        setSkipResetExpanded(true);
        queryClient.invalidateQueries('tabCounts'); // Invalidate tabCounts query
      });
    };

    return (
      <div className="d-flex">
        {windowWidth < 768 && (
          <InformationDoc
            buttonText={t('documents.table.docViewInformation')}
            disabled={false}
            fetchedData={fetchedData}
          />
        )}
        {buttons?.map(button => {
          const buttonDefaultText = button.disabled ? button.disabled_reason : button.text;
          const buttonText = hasLockedDocs ? t('documents.table.docActionsInProgress') : buttonDefaultText;

          if (button.type === 'send_to_employee')
            return (
              <SendDoc
                key={`${documentId}-send-to-employee`}
                type="send-to-employee"
                id={documentId}
                buttonText={buttonText}
                disabled={button.disabled || hasLockedDocs}
                invalidate={invalidateCallback}
              />
            );

          if (button.type === 'send_to_employer')
            return (
              <SendDoc
                key={`${documentId}-send`}
                type="send-to-employer"
                id={documentId}
                buttonText={buttonText}
                disabled={button.disabled || hasLockedDocs}
                invalidate={invalidateCallback}
              />
            );

          if (button.type === 'move_to_bin')
            return (
              <DeleteDoc
                key={`${documentId}-delete`}
                id={documentId}
                buttonText={buttonText}
                disabled={button.disabled || hasLockedDocs}
                invalidate={invalidateCallback}
              />
            );

          if (button.type === 'archive')
            return (
              <ArchiveDoc
                key={`${documentId}-archive`}
                id={documentId}
                entityName={entityName}
                buttonText={buttonText}
                disabled={button.disabled || hasLockedDocs}
                handlePreview={(docId: string) => handlePreviewDoc({ showModal, id: docId })}
                docHolderStatus={docHolderStatus}
                invalidate={invalidateCallback}
              />
            );

          if (button.type === 'review') {
            return (
              <ReviewDoc
                key={`${documentId}-review`}
                id={documentId}
                buttonText={
                  button?.disabled ? button?.disabled_reason ?? t('documents.table.actionReview') : button.text
                }
                disabled={!!button?.disabled}
                invalidate={invalidateCallback}
              />
            );
          }

          return null;
        })}
      </div>
    );
  };

  const buildTableFilters = useMemo(() => {
    const category = buildFilters({
      name: 'category_id',
      placeholder: t('table.categoryFilter'),
      options: [...(dropdowns?.category ?? [])],
      isMulti: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const creator = buildFilters({
      name: 'creator_id',
      placeholder: t('table.creatorFilter'),
      options: dropdowns?.creator,
      isMulti: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const user = buildFilters({
      name: 'user_id',
      placeholder: t('table.userFilter'),
      options: dropdowns?.user,
      isMulti: true,
      limitOptions: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const group = buildFilters({
      name: 'group',
      placeholder: t('table.groupFilter'),
      options: dropdowns?.group,
      isMulti: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const date = buildFilters({
      name: 'created_at',
      placeholder: t('table.createdAtFilter'),
      type: 'datepicker',
    });

    if (isEmployee) return [category, creator, date];

    const filters = [category, creator, user, group, date];

    if (!disableIconFilters) {
      const iconFilters = [];

      const showOnlyWaitingAction = buildFilters({
        name: 'only_waiting_action',
        placeholder: 'only_waiting_action',
        icon: (
          <CustomIconWait
            onClick={toggleSelectedOptionWait}
            isSelected={selectedOptionWait === 'true'}
          />
        ),
        type: 'icon',
        options: [
          { label: 'show', value: 'true', selected: selectedOptionWait === 'true' },
          { label: 'hide', value: 'false', selected: selectedOptionWait === 'false' },
        ],
      });

      const showLeaves = buildFilters({
        name: 'is_leave',
        placeholder: 'is_leave',
        icon: (
          <CustomIconLeaves
            onClick={toggleSelectedOptionLeaves}
            isSelected={selectedOptionLeaves === 'true'}
          />
        ),
        type: 'icon',
        options: [
          { label: 'show', value: '', selected: selectedOptionLeaves === 'true' },
          { label: 'hide', value: 'false', selected: selectedOptionLeaves === 'false' },
        ],
      });

      const showWorkTrips = buildFilters({
        name: 'is_work_trip',
        placeholder: 'is_work_trip',
        icon: (
          <CustomIconWorkTrips
            onClick={toggleSelectedOptionWorkTrips}
            isSelected={selectedOptionWorkTrips === 'true'}
          />
        ),
        type: 'icon',
        options: [
          { label: 'show', value: '', selected: selectedOptionWorkTrips === 'true' },
          { label: 'hide', value: 'false', selected: selectedOptionWorkTrips === 'false' },
        ],
      });

      iconFilters.push(showOnlyWaitingAction, showLeaves, showWorkTrips);

      if (isFeatureHealthAndSafetyEnabled) {
        const showHealthAndSafety = buildFilters({
          name: 'is_health_and_safety_training',
          placeholder: 'is_health_and_safety_training',
          icon: (
            <CustomIconHealthAndSafety
              onClick={toggleSelectedOptionHealthAndSafety}
              isSelected={selectedOptionHealthAndSafety === 'true'}
            />
          ),
          type: 'icon',
          options: [
            { label: 'show', value: '', selected: selectedOptionHealthAndSafety === 'true' },
            { label: 'hide', value: 'false', selected: selectedOptionHealthAndSafety === 'false' },
          ],
        });

        iconFilters.push(showHealthAndSafety);
      }

      filters.unshift(...iconFilters);
    }

    return filters;
  }, [
    CustomIconLeaves,
    CustomIconWorkTrips,
    CustomIconWait,
    disableIconFilters,
    dropdowns?.category,
    dropdowns?.creator,
    dropdowns?.group,
    dropdowns?.user,
    isEmployee,
    selectedOptionLeaves,
    selectedOptionWorkTrips,
    selectedOptionWait,
    selectedOptionHealthAndSafety,
    t,
  ]);

  // Filter visible columns according to `workMode`, document tab and props
  let filteredColumns = isMobile ? mobileColumns : columns;
  if (!isMobile) {
    if (isEmployee) filteredColumns = filteredColumns.filter(col => col.accessor !== 'user_name');
    if (hideActionsColumn) filteredColumns = filteredColumns.filter(col => col.accessor !== 'actions');
  }
  return (
    <FetchTable<IDocumentGroup>
      entityName={buildClientEntity(entityName, client)}
      entityEndpoint={entityEndpoint}
      loading={isLoadingDocDropdowns}
      searchParams={searchParams}
      columns={filteredColumns}
      isExpandable
      isSelectable={isSelectable}
      customSelectableRowProps={customSelectableRowProps}
      useCustomSelectBtn={useCustomSelectBtn}
      toggleSelectBtnLabel={toggleSelectBtnLabel}
      selectActionButtons={selectActionButtons}
      skipResetExpanded={skipResetExpanded}
      pagination
      disablePagination={disablePagination}
      pageLimit={10}
      filters={buildTableFilters}
      onDataFetched={handleDataFetched}
      showOnlyWaiting={selectedOptionWait === 'true'}
    />
  );
}

const Heading = styled.div`
  margin-left: 40px;
`;

export const IconWrapper = styled.div<IconWrapperProps>`
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &::after {
    content: '${props => (props.isSelected ? '' : '/')}';
    position: absolute;
    color: red;
    font-size: 26px;
    transform: rotate(30deg);
  }
`;

export default GroupedDocsTable;
